import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, KeyRound, ArrowLeft } from "lucide-react";
import { useT } from "@/context/LanguageContext";
import LanguageToggle from "@/components/app/LanguageToggle";
import PasswordInput from "@/components/app/PasswordInput";
import { toast } from "sonner";
import { api, detailText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { t } = useT();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [form, setForm] = useState({ phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [heroLine1, heroLine2] = t("brand.tagline_hero").split("\n");

  // State Lupa Password Modal
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1: input phone, 2: input OTP & new password
  const [forgotPhone, setForgotPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);

  const handleOpenForgot = () => {
    setForgotPhone(form.phone || "");
    setForgotStep(1);
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    setForgotOpen(true);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const cleanPhone = forgotPhone.trim();
    if (!cleanPhone) return toast.error("Masukkan nomor HP atau Email terdaftar");
    if (forgotLoading) return;
    setForgotLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { phone: cleanPhone });
      if (res.data?.otp_sent === false) {
        toast.warning(res.data?.message || "WA Service belum terhubung. Hubungi admin.");
      } else {
        toast.success(res.data?.message || "Kode OTP berhasil dikirim!");
        setForgotStep(2);
      }
    } catch (err) {
      const msg = detailText(err?.response?.data?.detail) || err?.message;
      toast.error(msg || "Gagal mengirim kode OTP. Pastikan akun terdaftar.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetWithOtp = async (e) => {
    e.preventDefault();
    const cleanPhone = forgotPhone.trim();
    if (!otp.trim()) return toast.error("Masukkan kode OTP");
    if (newPassword.length < 6) return toast.error("Password minimal 6 karakter");
    if (newPassword !== confirmPassword) return toast.error("Konfirmasi password tidak cocok");

    setForgotLoading(true);
    try {
      const res = await api.post("/auth/reset-password-otp", {
        phone: cleanPhone,
        otp: otp.trim(),
        new_password: newPassword,
      });
      toast.success(res.data?.message || "Password berhasil diperbarui!");
      setForgotOpen(false);
      setForm((f) => ({ ...f, phone: cleanPhone, password: "" }));
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Gagal mengubah password");
    } finally {
      setForgotLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleanPhone = form.phone.trim();
    if (!cleanPhone || !form.password) return toast.error("Nomor HP dan password wajib diisi");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { phone: cleanPhone, password: form.password });
      const data = await refresh();
      if (res.data?.user?.must_change_password) {
        navigate("/first-login");
      } else if (data?.is_admin) {
        navigate("/admin/pending");
      } else if (!data?.membership) {
        navigate("/activate");
      } else {
        navigate(data?.wedding?.setup_complete ? "/dashboard" : "/setup");
      }
    } catch (err) {
      toast.error(detailText(err?.response?.data?.detail) || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-[#FFFCF9] text-[#35282C] relative selection:bg-[#F9EFF0] selection:text-[#9E4053]">
      {/* Top Left Navigation Back to Landing */}
      <div className="absolute top-4 left-4 z-30 md:top-6 md:left-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#90777D] hover:text-[#BE5366] bg-white/80 backdrop-blur-md px-3.5 py-2 rounded-full border border-[#D3B68C]/30 shadow-sm transition-all"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>Kembali ke Beranda</span>
        </Link>
      </div>

      {/* Top Right Language Switcher */}
      <div className="absolute top-4 right-4 z-30 md:top-6 md:right-6">
        <LanguageToggle />
      </div>

      {/* Left Editorial Visual Banner (Desktop) */}
      <div className="relative hidden md:block overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1600&q=80"
          alt="Weddly Editorial Wedding"
          className="absolute inset-0 h-full w-full object-cover filter brightness-[0.7] contrast-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#35282C]/90 via-[#35282C]/50 to-[#9E4053]/30" />

        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/weddly-logo.png"
              alt="Weddly Logo"
              className="h-12 w-auto object-contain brightness-125 shrink-0"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-serif-title text-xl font-bold tracking-tight text-white">
                Wedding Planner
              </span>
              <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#D3B68C]">
                BY WEDDLY
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <span className="text-xs uppercase tracking-widest font-semibold text-[#D3B68C] block">
              ✦ Your Wedding, Beautifully Planned
            </span>
            <h1 className="font-serif-title text-4xl lg:text-5xl leading-tight font-bold">
              {heroLine1}<br />
              <span className="italic font-normal text-[#F9EFF0]">{heroLine2}</span>
            </h1>
            <p className="text-[#F9EFF0]/85 text-sm leading-relaxed max-w-md">
              {t("brand.tagline_sub")}
            </p>
          </div>
        </div>
      </div>

      {/* Right Login Form Workspace */}
      <div className="flex items-center justify-center p-6 sm:p-12 md:p-16 bg-[#FFFCF9]">
        <form onSubmit={submit} className="w-full max-w-md space-y-6 pt-12 md:pt-0">
          {/* Mobile Brand Logo */}
          <div className="flex items-center gap-3 md:hidden mb-4">
            <img
              src="/weddly-logo.png"
              alt="Weddly Logo"
              className="h-11 w-auto object-contain"
            />
            <div className="flex flex-col leading-tight">
              <span className="font-serif-title text-lg font-bold text-[#35282C]">
                Wedding Planner
              </span>
              <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#B89768]">
                BY WEDDLY
              </span>
            </div>
          </div>

          <div>
            <h2 className="font-serif-title text-3xl sm:text-4xl font-bold text-[#35282C]">
              {t("login.welcome")}
            </h2>
            <p className="text-sm text-[#90777D] mt-1.5 leading-relaxed">
              Masuk dengan nomor HP terdaftar & password Anda.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold text-[#35282C]">Nomor HP</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="Nomor HP"
                className="mt-1 border-[#F1E5E7] focus:border-[#BE5366] text-xs h-10 bg-white"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-bold text-[#35282C]">Password</Label>
                <button
                  type="button"
                  onClick={handleOpenForgot}
                  className="text-xs text-[#BE5366] hover:underline font-semibold"
                >
                  Lupa password?
                </button>
              </div>
              <PasswordInput
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-full text-sm font-semibold text-white bg-[#BE5366] hover:bg-[#9E4053] shadow-md hover:shadow-floating transition-all duration-200"
          >
            {loading ? "Memproses..." : "Masuk"}
          </Button>

          <p className="text-center text-xs text-[#90777D]">
            Belum punya akun?{" "}
            <Link to="/register" className="text-[#BE5366] font-bold hover:underline">
              Daftar Sekarang
            </Link>
          </p>

          <div className="pt-4 border-t border-[#F1E5E7] text-xs text-[#90777D] space-y-2">
            <div className="flex items-start gap-2">
              <Sparkles className="mt-0.5 h-4 w-4 text-[#BE5366] shrink-0" />
              <span>{t("login.license_note")}</span>
            </div>
          </div>
        </form>

        {/* Modal Lupa Password */}
        <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
          <DialogContent className="sm:max-w-md bg-[#FFFCF9] border-[#F1E5E7]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-serif-title text-xl text-[#35282C]">
                <KeyRound className="h-5 w-5 text-[#BE5366]" /> Reset Password Akun
              </DialogTitle>
            </DialogHeader>

            {forgotStep === 1 ? (
              <form onSubmit={handleSendOtp} className="space-y-4 pt-2">
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Masukkan nomor HP akun Weddly Anda. Kami akan mengirimkan kode OTP 6-digit via WhatsApp ke nomor terdaftar.
                </p>
                <div>
                  <Label className="text-xs font-bold text-[#35282C]">Nomor HP</Label>
                  <Input
                    value={forgotPhone}
                    onChange={(e) => setForgotPhone(e.target.value)}
                    placeholder="Nomor HP"
                    className="mt-1 border-[#F1E5E7] text-xs h-9 bg-white"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full rounded-full bg-[#BE5366] hover:bg-[#9E4053] text-white text-xs h-10 font-semibold shadow-md"
                >
                  {forgotLoading ? "Mengirim OTP..." : "Kirim Kode OTP via WhatsApp"}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetWithOtp} className="space-y-4 pt-2">
                <p className="text-xs text-[#90777D] leading-relaxed">
                  Kode OTP telah dikirim ke WA <strong>{forgotPhone}</strong>. Masukkan kode dan password baru Anda.
                </p>
                <div>
                  <Label className="text-xs font-bold text-[#35282C]">Kode OTP (6 angka)</Label>
                  <Input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="123456"
                    maxLength={6}
                    className="tracking-widest text-center text-lg font-mono mt-1 border-[#F1E5E7]"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#35282C]">Password Baru</Label>
                  <PasswordInput
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Minimal 6 karakter"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#35282C]">Konfirmasi Password Baru</Label>
                  <PasswordInput
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ulangi password baru"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setForgotStep(1)}
                    disabled={forgotLoading}
                    className="rounded-full text-xs"
                  >
                    Kirim Ulang OTP
                  </Button>
                  <Button
                    type="submit"
                    disabled={forgotLoading}
                    className="flex-1 rounded-full bg-[#BE5366] hover:bg-[#9E4053] text-white text-xs font-semibold shadow-md"
                  >
                    {forgotLoading ? "Memproses..." : "Ganti Password"}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
